/**
 * tripCostLogic.js
 *
 * Pure, component-free cost-computation logic for the Trip Cost editor
 * (feature: tripCostPhase1Updates). Extracting these functions out of
 * `TripCostEditor.vue` lets the quantity auto-population, slab/per-unit line
 * math, zero/blank exclusion, and totals be property-tested in isolation
 * without mounting a Vue component.
 *
 * This module is the scaffold only (task 1.1): the exported functions are
 * stubs whose real implementations are added in later tasks. The `num` helper
 * mirrors the numeric-coercion helper in `TripCostEditor.vue`.
 */

/**
 * Coerce a possibly-null/blank numeric field to a Number (defaulting to 0)
 * for line math. Mirrors the `num` helper in TripCostEditor.vue so the
 * extracted logic behaves identically.
 *
 * @param {*} val - the value to coerce
 * @returns {number} a finite number, or 0 when the value is not finite
 */
export function num(val) {
    const n = Number(val);
    return Number.isFinite(n) ? n : 0;
}

/**
 * Determine the auto-populated quantity for an additional service line from the
 * trip summary, matching only on `serviceCode` (never the payment model).
 *
 * Rules (serviceCode is compared case-insensitively):
 *   - `STOPS`         -> `orderCount` (billed in full — operator adjusts as needed)
 *   - `MILES`         -> `totalDistance`
 *   - `PICKUPMILES`   -> `pickupLegDistance` (SAMEDAY; depot->pickup road miles,
 *                        billed in full — operator adjusts the quantity as needed)
 *   - `DELIVERYMILES` -> `deliveryLegDistance` (SAMEDAY; pickup->delivery road miles)
 *   - serviceCode === `routeCode` -> `1` (flat per-route line)
 *   - anything else   -> `null` (blank/0 for operator entry)
 *
 * STOPS/MILES/routeCode use only `orderCount`, `totalDistance`, and `routeCode`.
 * PICKUPMILES/DELIVERYMILES additionally use the per-leg road distances
 * (`pickupLegDistance` from the TripWaypoint `sequence: 1` record and
 * `deliveryLegDistance` from the `sequence: 2` record), which the editor resolves
 * from the `tripWaypointByStop` GSI and passes in on `trip`. The rule never
 * inspects which payment model is selected — it is serviceCode-exact.
 *
 * @param {object} service - the service/product row (reads `serviceCode`)
 * @param {object} trip - trip summary values (`orderCount`, `totalDistance`,
 *   `routeCode`, `pickupLegDistance`, `deliveryLegDistance`)
 * @returns {number|null} the auto-populated quantity, or null for operator entry
 */
export function autoQuantityFor(service, trip) {
    const code = ((service && service.serviceCode) || "").toUpperCase();
    const summary = trip || {};
    const routeCode = (summary.routeCode || "").toUpperCase();

    // STOPS -> the trip's stop count, auto-populated from the waypoint summary orderCount
    // (billed as-is, matching how MILES uses totalDistance). The operator can adjust the
    // quantity before approval.
    if (code === "STOPS") return summary.orderCount ?? null;

    if (code === "MILES") return summary.totalDistance ?? null;

    // PICKUPMILES -> depot->pickup leg distance (billed in full). The operator can adjust the
    // quantity before approval.
    if (code === "PICKUPMILES") return summary.pickupLegDistance ?? null;

    // DELIVERYMILES -> pickup->delivery leg distance (billed in full).
    if (code === "DELIVERYMILES") return summary.deliveryLegDistance ?? null;

    if (routeCode && code === routeCode) return 1;

    return null; // blank/0 for operator entry
}

/**
 * Select the tier-price bracket that applies to a given count.
 *
 * Returns the first bracket whose `allowed !== false` and whose inclusive
 * `[start, end]` range contains `n` (i.e. `start <= n <= end`). Returns `null`
 * when `tierPrice` is absent/empty, when `n` is not a finite number, or when no
 * bracket matches — in which case callers fall back to the flat `Product.price`
 * (per-unit) or 0.
 *
 * @param {Array<object>} tierPrice - the bracket list ({ start, end, price, slab, allowed })
 * @param {number} n - the count used to select the bracket
 * @returns {object|null} the matching bracket, or null when none match
 */
export function selectBracket(tierPrice, n) {
    if (!Array.isArray(tierPrice) || tierPrice.length === 0) {
        return null;
    }

    const count = Number(n);
    if (!Number.isFinite(count)) {
        return null;
    }

    for (const bracket of tierPrice) {
        if (!bracket || bracket.allowed === false) {
            continue;
        }

        const start = Number(bracket.start);
        const end = Number(bracket.end);
        if (!Number.isFinite(start) || !Number.isFinite(end)) {
            continue;
        }

        if (count >= start && count <= end) {
            return bracket;
        }
    }

    return null;
}

/**
 * Determine whether a line uses slab pricing.
 *
 * A line is slab when any bracket on its `tierPrice` has `slab === true`. An
 * absent/empty `tierPrice` is per-unit (returns false), falling back to the
 * flat `Product.price`.
 *
 * @param {object} line - the line/service row (reads `tierPrice`)
 * @returns {boolean} true when any bracket on the line's tierPrice has slab === true
 */
export function isSlabLine(line) {
    const tierPrice = line && line.tierPrice;
    if (!Array.isArray(tierPrice) || tierPrice.length === 0) {
        return false;
    }

    return tierPrice.some((bracket) => bracket && bracket.slab === true);
}

/**
 * Compute the per-line cost math for a single working cost line.
 *
 * Determines whether the line is slab or per-unit (via `isSlabLine`) and
 * computes `price`, `quantity`, and `amount` accordingly, then the fuel
 * surcharge and total that apply to both modes (Requirement 6).
 *
 * Per-unit (`tierPrice.slab` absent/false — Requirement 6.1):
 *   - `price`  = bracket matching the quantity's price, else the flat
 *                `Product.price` (`line.price`) when no bracket matches
 *   - `amount` = `price × quantity`
 *
 * Slab (`tierPrice.slab === true` — Requirements 6.2, 6.3):
 *   - `quantity` = 1 (forced)
 *   - bracket    = bracket whose inclusive `[start, end]` contains the stop
 *                  count (`trip.orderCount`)
 *   - `price`    = the selected bracket's flat amount (`bracket.price`), or 0
 *                  when no bracket matches
 *   - `amount`   = `price × 1`
 *
 * Both modes (Requirements 6.4, 6.5):
 *   - `fuelSurcharge` = `amount × fuelSurchargeRate` (missing rate coerced to 0)
 *   - `total`         = `amount + fuelSurcharge`
 *
 * All numeric inputs are coerced via `num` so a missing/blank/non-numeric
 * price, quantity, or fuel-surcharge rate never produces NaN.
 *
 * @param {object} line - the working cost line (reads `tierPrice`, `price`,
 *   `quantity`, `fuelSurchargeRate`)
 * @param {object} trip - trip summary values (`orderCount` selects the slab bracket)
 * @returns {object} the line with computed `price`, `quantity`, `amount`,
 *   `fuelSurcharge`, and `total`
 */
export function computeLine(line, trip) {
    const row = line || {};
    const summary = trip || {};
    const fuelSurchargeRate = num(row.fuelSurchargeRate);

    let price;
    let quantity;

    if (isSlabLine(row)) {
        // Slab: quantity forced to 1, bracket selected by the stop count.
        quantity = 1;
        const bracket = selectBracket(row.tierPrice, summary.orderCount);
        price = bracket ? num(bracket.price) : 0;
    } else {
        // Per-unit: price from the quantity-matching bracket, else flat price.
        quantity = num(row.quantity);
        const bracket = selectBracket(row.tierPrice, quantity);
        price = bracket ? num(bracket.price) : num(row.price);
    }

    const amount = price * quantity;
    const fuelSurcharge = amount * fuelSurchargeRate;
    const total = amount + fuelSurcharge;

    return {
        ...row,
        price,
        quantity,
        amount,
        fuelSurcharge,
        total,
    };
}

/**
 * Build the persisted cost node from the working cost lines: per-unit/slab line
 * math, zero/blank exclusion, and trip totals.
 *
 * Each working line is first run through `computeLine` (per-unit/slab math).
 * Then any line whose quantity is `0`, blank, or non-numeric is excluded before
 * summing — `num` coerces blank/non-numeric values to 0, so the single
 * `num(quantity) > 0` guard covers all three cases (Requirements 5.1, 5.2, 5.3).
 * Because `computeLine` forces slab lines to `quantity: 1`, slab lines are never
 * excluded. The totals are then summed over the kept lines only
 * (Requirements 6.6, 6.7).
 *
 * @param {Array<object>} lines - the working cost lines
 * @param {object} trip - trip summary values used for slab bracket selection
 * @returns {{ lines: Array<object>, tripTotal: number, fuelSurcharge: number }}
 */
export function buildCostNode(lines, trip) {
    const working = Array.isArray(lines) ? lines : [];
    const computed = working.map((line) => computeLine(line, trip));

    // Exclude zero/blank/non-numeric quantity lines before summing totals.
    // `num` coerces blank/non-numeric to 0, so `> 0` covers 0, blank, and
    // non-numeric (Requirements 5.1, 5.2, 5.3). Slab lines force quantity: 1
    // in computeLine, so they are never excluded.
    const kept = computed.filter((line) => num(line.quantity) > 0);

    const tripTotal = kept.reduce((sum, line) => sum + line.total, 0);
    const fuelSurcharge = kept.reduce((sum, line) => sum + line.fuelSurcharge, 0);

    return { lines: kept, tripTotal, fuelSurcharge };
}
